import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type UploadStatus = 'uploading' | 'success' | 'error';

// All available upload categories
export type UploadCategory = 
  | 'logo' 
  | 'identity' 
  | 'kooperation' 
  | 'sonstiges' 
  | 'antrag' 
  | 'ausweisdokumente' 
  | 'beratungsdokumente' 
  | 'polizze' 
  | 'angebot_marktvergleich'
  | 'auskunftsvollmacht'
  | 'prozessvollmacht'
  | 'attachment'
  | 'belege'
  | 'dokumente'
  | 'wissensdatenbank'
  | 'pdf';

// All available dialog types
export type UploadDialogType = 
  | 'partner' 
  | 'customer' 
  | 'contract'
  | 'lead'
  | 'fremdvertrag'
  | 'wissensdatenbank'
  | 'aufgabe'
  | 'intervention'
  | 'calendar'
  | 'access'
  | 'abrechnung'
  | 'buchhaltung';

// All available buckets
export type UploadBucket = 
  | 'partner-logos' 
  | 'contract-documents'
  | 'wissensdatenbank'
  | 'vb-pdfs';

export interface UploadItem {
  id: string;
  fileName: string;
  file: File;
  targetPath: string;
  status: UploadStatus;
  progress: number;
  error?: string;
  url?: string;
  partnerId: string;
  partnerName?: string;
  category: UploadCategory;
  dialogType: UploadDialogType;
  bucket: UploadBucket;
  retryCount?: number;
}

interface StartUploadOptions {
  partnerName?: string;
  dialogType?: UploadDialogType;
  bucket?: UploadBucket;
}

interface UploadContextType {
  uploads: UploadItem[];
  startUpload: (file: File, partnerId: string, category: UploadCategory, targetPath: string, options?: StartUploadOptions) => Promise<string | null>;
  removeUpload: (id: string) => void;
  retryUpload: (id: string) => Promise<void>;
  getUploadsByPartner: (partnerId: string, category?: UploadCategory) => UploadItem[];
  cancelUpload: (id: string) => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

const MAX_RETRY_COUNT = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Helper to determine bucket from category
const getBucketFromCategory = (category: UploadCategory): UploadBucket => {
  switch (category) {
    case 'logo':
      return 'partner-logos';
    case 'wissensdatenbank':
      return 'wissensdatenbank';
    default:
      return 'contract-documents';
  }
};

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const { toast } = useToast();
  const xhrRefs = useRef<Map<string, XMLHttpRequest>>(new Map());

  const getSupabaseUrl = () => {
    // Lovable projects must not rely on VITE_* env vars; use the configured project URL.
    return 'https://hwjnhvbrkvzumzhakgla.supabase.co';
  };

  const getAccessToken = async (): Promise<string | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  };

  const uploadWithProgress = useCallback(async (
    uploadId: string,
    file: File,
    targetPath: string,
    bucket: UploadBucket,
    retryCount: number = 0
  ): Promise<{ path: string }> => {
    return new Promise(async (resolve, reject) => {
      console.debug('[Upload] start', { uploadId, bucket, targetPath, name: file.name, size: file.size, type: file.type });
      const accessToken = await getAccessToken();
      if (!accessToken) {
        reject(new Error('Nicht authentifiziert'));
        return;
      }

      const xhr = new XMLHttpRequest();
      xhrRefs.current.set(uploadId, xhr);

      // Real progress tracking
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploads(prev => prev.map(u =>
            u.id === uploadId ? { ...u, progress } : u
          ));
        }
      };

      xhr.onload = () => {
        xhrRefs.current.delete(uploadId);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // Construct the full public URL for the uploaded file
          const supabaseUrl = getSupabaseUrl();
          const publicUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${targetPath}`;
          resolve({ path: publicUrl });
        } else if (xhr.status === 503 && retryCount < MAX_RETRY_COUNT) {
          // Automatic retry with exponential backoff for 503 errors
          const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
          console.log(`[Upload] 503 error, retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRY_COUNT})`);
          
          setUploads(prev => prev.map(u =>
            u.id === uploadId 
              ? { ...u, progress: 0, retryCount: retryCount + 1 } 
              : u
          ));

          setTimeout(async () => {
            try {
              const result = await uploadWithProgress(uploadId, file, targetPath, bucket, retryCount + 1);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }, delay);
        } else {
          let errorMessage = `Upload fehlgeschlagen: ${xhr.status}`;
          try {
            const response = JSON.parse(xhr.responseText);
            errorMessage = response.message || response.error || errorMessage;
          } catch {
            // Ignore parse errors
          }
          reject(new Error(errorMessage));
        }
      };

      xhr.onerror = () => {
        xhrRefs.current.delete(uploadId);
        
        if (retryCount < MAX_RETRY_COUNT) {
          // Retry on network errors
          const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
          console.log(`[Upload] Network error, retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRY_COUNT})`);
          
          setUploads(prev => prev.map(u =>
            u.id === uploadId 
              ? { ...u, progress: 0, retryCount: retryCount + 1 } 
              : u
          ));

          setTimeout(async () => {
            try {
              const result = await uploadWithProgress(uploadId, file, targetPath, bucket, retryCount + 1);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }, delay);
        } else {
          reject(new Error('Netzwerkfehler beim Upload'));
        }
      };

      xhr.ontimeout = () => {
        xhrRefs.current.delete(uploadId);
        reject(new Error('Upload-Timeout'));
      };

      // Set timeout for large files (5 minutes)
      xhr.timeout = 300000;

      const supabaseUrl = getSupabaseUrl();
      const uploadUrl = `${supabaseUrl}/storage/v1/object/${bucket}/${targetPath}`;

      xhr.open('POST', uploadUrl, true);
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      xhr.setRequestHeader('x-upsert', 'true');
      // Supabase storage works without explicit content-type, but setting it improves compatibility
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      // Send file directly (not FormData for Supabase Storage)
      xhr.send(file);
    });
  }, []);

  const cancelUpload = useCallback((id: string) => {
    const xhr = xhrRefs.current.get(id);
    if (xhr) {
      xhr.abort();
      xhrRefs.current.delete(id);
    }
    setUploads(prev => prev.filter(u => u.id !== id));
  }, []);

  const startUpload = useCallback(async (
    file: File,
    partnerId: string,
    category: UploadCategory,
    targetPath: string,
    options?: StartUploadOptions
  ): Promise<string | null> => {
    const uploadId = `${partnerId}-${category}-${Date.now()}-${Math.random()}`;

    // Ensure encoded original segment in our naming scheme and pick correct bucket
    const ensureEncodedKey = (path: string) => {
      try {
        const lastSlash = path.lastIndexOf('/');
        const dir = lastSlash >= 0 ? path.slice(0, lastSlash + 1) : '';
        const filePart = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;
        const dot = filePart.lastIndexOf('.');
        const base = dot >= 0 ? filePart.slice(0, dot) : filePart;
        const ext = dot >= 0 ? filePart.slice(dot) : '';
        const parts = base.split('_');
        if (parts.length >= 4) {
          const nameIdx = parts.length - 2;
          const maybeEncoded = parts[nameIdx];
          const isEncoded = /%[0-9A-Fa-f]{2}/.test(maybeEncoded);
          const encoded = isEncoded ? maybeEncoded : encodeURIComponent(maybeEncoded);
          parts[nameIdx] = encoded;
          return dir + parts.join('_') + ext;
        }
        return path;
      } catch {
        return path;
      }
    };

    // Use provided bucket or determine from category
    const bucket: UploadBucket = options?.bucket || getBucketFromCategory(category);
    const finalTargetPath = ensureEncodedKey(targetPath);
    
    const newUpload: UploadItem = {
      id: uploadId,
      fileName: file.name,
      file,
      targetPath: finalTargetPath,
      status: 'uploading',
      progress: 0,
      partnerId,
      partnerName: options?.partnerName,
      category,
      dialogType: options?.dialogType || 'partner',
      bucket,
      retryCount: 0,
    };

    setUploads(prev => [...prev, newUpload]);

    try {
      const result = await uploadWithProgress(uploadId, file, finalTargetPath, bucket, 0);

      setUploads(prev => prev.map(u =>
        u.id === uploadId
          ? { ...u, status: 'success', progress: 100, url: result.path }
          : u
      ));

      return result.path;
    } catch (error: any) {
      setUploads(prev => prev.map(u =>
        u.id === uploadId
          ? { ...u, status: 'error', progress: 0, error: error.message }
          : u
      ));

      toast({
        title: "Upload fehlgeschlagen",
        description: `${file.name}: ${error.message}`,
        variant: "destructive",
      });

      return null;
    }
  }, [toast, uploadWithProgress]);

  const removeUpload = useCallback((id: string) => {
    cancelUpload(id);
    setUploads(prev => prev.filter(u => u.id !== id));
  }, [cancelUpload]);

  const retryUpload = useCallback(async (id: string) => {
    const upload = uploads.find(u => u.id === id);
    if (!upload) return;

    setUploads(prev => prev.map(u =>
      u.id === id
        ? { ...u, status: 'uploading', progress: 0, error: undefined, retryCount: 0 }
        : u
    ));

    try {
      const result = await uploadWithProgress(id, upload.file, upload.targetPath, upload.bucket, 0);

      setUploads(prev => prev.map(u =>
        u.id === id
          ? { ...u, status: 'success', progress: 100, url: result.path }
          : u
      ));

      toast({
        title: "Upload erfolgreich",
        description: `${upload.fileName} wurde hochgeladen.`,
      });
    } catch (error: any) {
      setUploads(prev => prev.map(u =>
        u.id === id
          ? { ...u, status: 'error', progress: 0, error: error.message }
          : u
      ));

      toast({
        title: "Upload fehlgeschlagen",
        description: `${upload.fileName}: ${error.message}`,
        variant: "destructive",
      });
    }
  }, [uploads, toast, uploadWithProgress]);

  const getUploadsByPartner = useCallback((partnerId: string, category?: UploadCategory) => {
    return uploads.filter(u => 
      u.partnerId === partnerId && (!category || u.category === category)
    );
  }, [uploads]);

  return (
    <UploadContext.Provider value={{ uploads, startUpload, removeUpload, retryUpload, getUploadsByPartner, cancelUpload }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUploadManager = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUploadManager must be used within UploadProvider');
  }
  return context;
};
